import React, { useEffect } from 'react';

import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import YouTube, { YouTubeEvent } from 'react-youtube';

import ListItemButtonLink from '../ListItemButtonLink';
import YouTubeVideo from '../YouTubeVideo';

import {
  ColumnContainer,
  DescriptionWrapper,
  IconWrapper,
  ListIndexLabel,
  ListLabel,
  OpenInIcon,
  OpenInIconWrapper,
  PlaceholderText,
  PlayerColumn,
  SidebarHeader,
  SidebarHeaderWrapper,
  TitleBarContainer,
  UtilColumn,
} from './index.styles';
import {
  ResourceType,
  Video, VideoPlayerProps,
} from './types';

type Resource = {
  label: string,
  href: string,
  type?: ResourceType,
}

interface TitleBarProps {
  title: string,
  host: string,
  uploadDatetime: number,
}

const TitleBar = ({ title, host, uploadDatetime } : TitleBarProps) => (
  <TitleBarContainer>
    <Typography variant="h5">{title}</Typography>
    <Typography variant="caption">{host} · {dayjs(uploadDatetime).format('DD MMM YYYY')}</Typography>
  </TitleBarContainer>
);

interface SideBarProps {
  resources?: Resource[],
  topicVideos?: Video[],
  relatedVideos?: Video[],
  currentVideoId: string,
  getVideoHref: (subject: string, videoId: string) => string,
}

const SideBar = ({
  resources = [],
  topicVideos = [],
  relatedVideos = [],
  currentVideoId,
  getVideoHref,
} : SideBarProps) => (
  <>
    {resources.length > 0 && (
      <List subheader={(
        <SidebarHeaderWrapper disableSticky>
          <SidebarHeader variant="overline">
            RESOURCES
          </SidebarHeader>
        </SidebarHeaderWrapper>
      )}
      >
        {resources.map((res) => (
          <ListItem
            disablePadding
            key={`${res.type}-${res.label}`}
          >
            <ListItemButtonLink
              href={res.href}
              target="_blank"
            >
              <ListItemIcon>
                <IconWrapper>
                  <AttachFileRoundedIcon />
                </IconWrapper>
              </ListItemIcon>
              <ListItemText primary={(
                <ListLabel variant="body2">
                  {res.label}
                </ListLabel>
              )}
              />
              <OpenInIconWrapper>
                <OpenInIcon />
              </OpenInIconWrapper>
            </ListItemButtonLink>
          </ListItem>
        ))}
      </List>
    )}
    <List subheader={(
      <SidebarHeaderWrapper disableSticky>
        <SidebarHeader variant="overline">
          TOPIC VIDEOS
        </SidebarHeader>
      </SidebarHeaderWrapper>
    )}
    >
      {topicVideos.length > 0 ? topicVideos.map((vid, index) => (
        <ListItem
          disablePadding
          key={vid.videoId}
        >
          <ListItemButtonLink
            href={getVideoHref(vid.subject, vid.videoId)}
          >
            <ListItemIcon>
              <ListIndexLabel variant="body2" selected={currentVideoId === vid.videoId}>
                {index + 1}
              </ListIndexLabel>
            </ListItemIcon>
            <ListItemText primary={(
              <ListLabel variant="body2" selected={currentVideoId === vid.videoId}>
                {vid.title}
              </ListLabel>
            )}
            />
          </ListItemButtonLink>
        </ListItem>
      )) : (
        <PlaceholderText>
          <Typography variant="caption">
            No Topic Videos
          </Typography>
        </PlaceholderText>
      )}
    </List>
    {relatedVideos.length > 0 && (
      <List subheader={(
        <SidebarHeaderWrapper disableSticky>
          <SidebarHeader variant="overline">
            RELATED VIDEOS
          </SidebarHeader>
        </SidebarHeaderWrapper>
      )}
      >
        {relatedVideos.map((vid, index) => (
          <ListItem
            disablePadding
            key={vid.videoId}
          >
            <ListItemButtonLink
              href={getVideoHref(vid.subject, vid.videoId)}
            >
              <ListItemIcon>
                <ListIndexLabel variant="body2">
                  {index + 1}
                </ListIndexLabel>
              </ListItemIcon>
              <ListItemText primary={(
                <ListLabel variant="body2">
                  {vid.title}
                </ListLabel>
              )}
              />
            </ListItemButtonLink>
          </ListItem>
        ))}
      </List>
    )}
  </>
);

const VideoPlayer = (
  { videoId,
    youtubeId,
    title,
    description,
    host,
    uploadDatetime,
    resources = [],
    topicVideos = [],
    relatedVideos = [],
    onVideoEnd: onVideoEndProp = () => {},
    getVideoHref = () => '',
    autoplay = false }: VideoPlayerProps,
) => {
  const theme = useTheme();

  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const onVideoEnd = () => {
    if (autoplay) {
      // Play the next video in the topic
      const currIndex = topicVideos.findIndex((x) => x.videoId === videoId);
      if (currIndex + 1 < topicVideos.length) {
        onVideoEndProp(topicVideos[currIndex + 1].subject,
          topicVideos[currIndex + 1].videoId);
      }
    }
  };

  useEffect(() => {
    const overrideClipboard = setInterval(async () => {
      // Override user clipboard every 400 ms to make it annoying to copy URL
      // Only do this if the clipboard content is a YouTube URL
      const clipboardText = await navigator.clipboard.readText();
      if (document.hasFocus() && (clipboardText.includes('youtu.be') || clipboardText.includes('youtube.com'))) {
        await navigator.clipboard.writeText('');
      }
    }, 400);

    // Clean up on dismount
    return () => clearInterval(overrideClipboard);
  },
  []);

  return (
    <ColumnContainer stack={!isMd}>
      <PlayerColumn stack={!isMd}>
        <YouTubeVideo
          videoId={youtubeId}
          autoplay
          onStateChange={(e: YouTubeEvent) =>
            e.data === YouTube.PlayerState.ENDED && onVideoEnd()}
        />
        <TitleBar
          title={title}
          host={host}
          uploadDatetime={uploadDatetime}
        />
        {description && (
          <>
            <Divider />
            <DescriptionWrapper>
              <Typography variant="body2">{description}</Typography>
            </DescriptionWrapper>
          </>
        )}
        {!isMd && <Divider />}
      </PlayerColumn>
      <UtilColumn stack={!isMd}>
        <SideBar
          resources={resources}
          topicVideos={topicVideos}
          relatedVideos={relatedVideos}
          currentVideoId={videoId}
          getVideoHref={getVideoHref}
        />
      </UtilColumn>
    </ColumnContainer>
  );
};
export default VideoPlayer;
